import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Toolbar,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { filterRows, sortRows } from "../../utils/tableUtils";
import { tokens } from "../../utils/figmaTokens";

// Presentational table primitive: search + sort + render only. It owns its
// own search/sort state; it never fetches and knows nothing about Firestore
// — pages feed it rows (typically via useCollection) and it stays dumb.
export const DataTable = ({
  columns,
  rows,
  searchKeys = [],
  initialSort,
  onRowClick,
  emptyText = "No records found",
  headerActions,
  loading = false,
  error = "",
}) => {
  const [term, setTerm] = useState("");
  const [sortField, setSortField] = useState(initialSort?.field || "");
  const [sortDirection, setSortDirection] = useState(
    initialSort?.direction || "asc",
  );

  const visibleRows = useMemo(() => {
    const filtered = filterRows(rows, term, searchKeys);
    return sortField
      ? sortRows(filtered, sortField, sortDirection)
      : filtered;
  }, [rows, term, searchKeys, sortField, sortDirection]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  return (
    <Paper
      sx={{
        width: "100%",
        border: `1px solid ${tokens.border.default}`,
        borderRadius: "12px",
        boxShadow: "none",
        overflow: "hidden",
      }}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : (
        <>
          <Toolbar sx={{ gap: "8px", flexWrap: "wrap", py: 2 }}>
            {searchKeys.length > 0 && (
              <TextField
                size="small"
                placeholder="Search..."
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: tokens.icon.default }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: "8px", fontSize: 14 },
                }}
                sx={{ minWidth: { xs: "100%", sm: 320 } }}
              />
            )}
            {headerActions && (
              <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
                {headerActions}
              </Box>
            )}
          </Toolbar>

          <TableContainer>
            <Table>
              <TableHead
                sx={{
                  background: tokens.surface.muted,
                  "& .MuiTableCell-root": { py: 1.5 },
                }}
              >
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{ fontWeight: 400, fontSize: 14, color: tokens.text.primary }}
                    >
                      {col.sortable ? (
                        <TableSortLabel
                          active={sortField === col.key}
                          direction={
                            sortField === col.key ? sortDirection : "asc"
                          }
                          onClick={() => toggleSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {emptyText}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((row, i) => (
                    <TableRow
                      key={row.id ?? i}
                      hover={Boolean(onRowClick)}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      sx={{
                        cursor: onRowClick ? "pointer" : "default",
                        "& .MuiTableCell-root": { py: 2 },
                      }}
                    >
                      {columns.map((col) => (
                        <TableCell key={col.key} sx={{ fontSize: 14 }}>
                          {col.render ? col.render(row) : row[col.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
};

export default DataTable;
